import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    sessionHeartbeat,
    sessionLogin,
    getSessionId,
    getSessionUserId,
} from "@/services/sessionClient";
import { endSession, getAuthToken, isTokenExpired } from "@/utility/authSession";
import useAuthUser from "@/hooks/useAuthUser";
import { useAuth } from "@/config/AuthContext";

/**
 * Silent component: while the user is authenticated it
 *  - checks the local token expiry (no network) so an expired session is
 *    caught as soon as the tab ticks or regains focus, and
 *  - polls /api/session/heartbeat every 15s. If the backend says the session
 *    was kicked (a login happened on another device), sign this tab out with a
 *    toast and redirect to /login.
 *
 * Non-kick heartbeat failures (unknown session, tracker blip) must NOT sign
 * the user out — that caused false "automatic logout" when nobody signed in
 * elsewhere. Those cases try a silent session rebind instead.
 *
 * Every sign-out here is local to this tab. Shared auth storage means a
 * broadcast would clear whatever session is current at that moment, including
 * one a different tab has just signed into.
 */
const SessionGuard = () => {
    const user = useAuthUser();
    const navigate = useNavigate();
    const { setIsAuthenticated } = useAuth();

    useEffect(() => {
        const userId = getSessionUserId(user);
        const hasUser = Boolean(userId && userId !== "unknown");

        let cancelled = false;
        // Avoid rebind spam when the tracker is down.
        let rebindInFlight = false;
        let lastRebindAt = 0;
        // The session this instance polices, identified by the tracked session
        // id rather than the token: extending a session reissues the token,
        // while a fresh sign-in is what mints a new session id.
        let watchedSessionId = getSessionId();

        /** Who shared storage currently belongs to, or null when unreadable. */
        const storedUserId = () => {
            try {
                const parsed = JSON.parse(localStorage.getItem("authUser") || "null");
                const data = Array.isArray(parsed) ? parsed[0] : parsed;
                return data ? getSessionUserId(data) : null;
            } catch {
                return null;
            }
        };

        /**
         * True once a different session owns storage. This instance then stands
         * down instead of acting: a tab woken up by someone else's sign-in still
         * holds the previous user in its own store, and enforcing against that
         * would sign the new session out for every tab.
         */
        const sessionReplaced = () => {
            if (!getAuthToken()) return false;

            const owner = storedUserId();
            if (hasUser && owner && owner !== userId) {
                cancelled = true;
                return true;
            }

            const sessionId = getSessionId();
            if (!watchedSessionId) {
                watchedSessionId = sessionId;
                return false;
            }
            if (!sessionId || sessionId === watchedSessionId) return false;
            cancelled = true;
            return true;
        };

        const forceSignOut = (message, reason, token) => {
            if (cancelled) return;
            // Clear this tab only — broadcasting would order the other tabs out
            // too, and `expectedToken` keeps even this clear from touching a
            // session that was created after the verdict was reached.
            if (!endSession({ reason, broadcast: false, expectedToken: token })) {
                cancelled = true;
                return;
            }
            cancelled = true;
            setIsAuthenticated?.(false);
            toast.error(message || "You have been signed out — signed in on another device.", { duration: 12000 });
            navigate("/login", { replace: true });
        };

        /** Local, synchronous expiry check. Returns true when the session ended. */
        const expiredLocally = () => {
            const token = getAuthToken();
            if (!token || isTokenExpired(token)) {
                forceSignOut("Your session has expired. Please sign in again.", "expired", token);
                return true;
            }
            return false;
        };

        /**
         * Tracker lost our session row (deploy/DB clear/id mismatch) but JWT is
         * still good — re-register on this device without kicking anyone else.
         */
        const trySilentRebind = async () => {
            if (rebindInFlight || cancelled || !hasUser) return;
            const now = Date.now();
            if (now - lastRebindAt < 60000) return;
            rebindInFlight = true;
            lastRebindAt = now;
            try {
                const res = await sessionLogin({ user, force: false });
                if (cancelled) return;
                if (res?.success && res?.session_id) {
                    watchedSessionId = res.session_id;
                    return;
                }
                // Conflict means another device really holds the seat — leave
                // the user alone until that device force-login kicks us (or
                // they sign in again). Do not force-logout from here.
            } catch (err) {
                console.warn("Session rebind skipped:", err?.message || err);
            } finally {
                rebindInFlight = false;
            }
        };

        const tick = async () => {
            if (cancelled) return;
            if (sessionReplaced()) return;
            if (expiredLocally()) return;
            if (!hasUser) return;

            // Read per tick, not once at mount: a tab woken up by a sign-in in
            // another tab starts out with no session id yet.
            const token = getAuthToken();
            const sessionId = getSessionId();
            if (!sessionId) {
                // Logged in without a tracker row (tracker was down at login).
                await trySilentRebind();
                return;
            }

            try {
                const res = await sessionHeartbeat(userId);
                if (cancelled) return;
                // A sign-in may have landed while the request was in flight,
                // in which case this answer describes a session that is gone.
                if (getAuthToken() !== token || getSessionId() !== sessionId) return;

                if (res.valid) {
                    return;
                }

                // Only an explicit kick means "signed in on another device".
                if (res.kicked) {
                    forceSignOut(
                        "Signed out — same account signed in on another device.",
                        "signed-out",
                        token,
                    );
                    return;
                }

                // Unknown / inactive tracker row — heal if JWT still valid.
                // Never treat this as a kick; that caused automatic logouts
                // when nobody signed in elsewhere.
                if (!isTokenExpired(token)) {
                    await trySilentRebind();
                }
            } catch (_) { /* ignore transient */ }
        };

        const onFocus = () => {
            if (cancelled || sessionReplaced()) return;
            expiredLocally();
        };
        const onVisible = () => {
            if (document.visibilityState === "visible") onFocus();
        };

        tick(); // fire once immediately
        const iv = setInterval(tick, 15000);
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            cancelled = true;
            clearInterval(iv);
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [user, navigate, setIsAuthenticated]);

    return null;
};

export default SessionGuard;
